// Step 26.5: shared dialog + toast UI for the editor reject flow.
// Driven by the `useReject` hook in useReject.js. Surface-agnostic — uses
// hook state for moment/dialog/toast, no local data of its own.
//
// Categories must stay in sync with:
//   - backend validator in tbtv-highlights/src/routes/dropped-moments.js
//   - migration CHECK constraint in 0023_dropped_moments_reason.sql
// Only `hallucinated` and `recap` feed Step 29's RAG injection.

import { useState } from 'react'
import { XCircle, Undo2 } from 'lucide-react'

const REASON_OPTIONS = [
  { value: 'hallucinated', label: 'Hallucinated', tip: "The cited cue text doesn't describe this play. Stats commentary, intro chatter, recap of a past game." },
  { value: 'recap',        label: 'Recap',        tip: 'Real play, but it already happened earlier in this same broadcast. Pick this if the announcer is referencing the play rather than narrating it live.' },
  { value: 'wrong_type',   label: 'Wrong type',   tip: 'Real moment but tagged as the wrong moment type (see the chip filters above for the valid types for this sport).' },
  { value: 'marginal',     label: 'Marginal',     tip: 'Real but not reel-worthy in your editorial judgment. Use sparingly.' },
  { value: 'wrong_window', label: 'Wrong window', tip: 'Real moment but the clip bounds are wrong. Consider trimming in the Reel Editor instead.' },
  { value: 'quality',      label: 'Quality',      tip: 'Audio dropout, blank video, missing context.' },
  { value: 'other',        label: 'Other',        tip: 'None of the above. Note required.' },
]

function fmtMs(ms) {
  const totalS = Math.floor((ms || 0) / 1000)
  const m = Math.floor(totalS / 60)
  const s = totalS % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function RejectMomentDialog({ hook }) {
  const [category, setCategory] = useState('hallucinated')
  const [note, setNote] = useState('')
  const m = hook.rejectingMoment
  if (!m) return null
  const requiresNote = category === 'other'
  const canSubmit = !!category && (!requiresNote || note.trim().length > 0)
  const label = `${m.title || '(untitled)'} @ ${fmtMs(m.start_ms)}`
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={hook.cancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8,
        padding: 18, width: 'min(520px, 92vw)', display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ fontSize: 13, color: '#fca5a5', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <XCircle size={14} /> Reject moment
        </div>
        <div style={{ fontSize: 12, color: '#ddd' }}>
          <span style={{ color: '#888' }}>Moment:</span> {label}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {REASON_OPTIONS.map(o => (
            <label key={o.value} title={o.tip}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                padding: '6px 8px',
                background: category === o.value ? 'rgba(248,113,113,0.08)' : 'transparent',
                border: '1px solid ' + (category === o.value ? '#7f1d1d' : '#1e293b'),
                borderRadius: 4, cursor: 'pointer',
              }}>
              <input
                type="radio"
                name="reject-reason"
                value={o.value}
                checked={category === o.value}
                onChange={() => setCategory(o.value)}
                style={{ marginTop: 2 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: '#ddd', fontWeight: 500 }}>{o.label}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2, lineHeight: 1.4 }}>{o.tip}</div>
              </div>
            </label>
          ))}
        </div>
        <div>
          <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
            Note {requiresNote ? <span style={{ color: '#fca5a5' }}>(required for "Other")</span> : <span>(optional)</span>}
          </label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={requiresNote ? 'Why are you rejecting this?' : 'Add a note (optional)'}
            style={{
              width: '100%', padding: '6px 8px', fontSize: 12,
              background: '#0d1622', border: '1px solid #1e293b', borderRadius: 4, color: '#ddd',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={hook.cancel}
            style={{
              padding: '6px 12px', fontSize: 12,
              background: 'transparent', border: '1px solid #333', borderRadius: 4,
              color: '#bfdbfe', cursor: 'pointer',
            }}>Cancel</button>
          <button
            onClick={() => hook.confirm({ reason_category: category, reason_note: note.trim() || null })}
            disabled={!canSubmit}
            style={{
              padding: '6px 14px', fontSize: 12,
              background: canSubmit ? '#7f1d1d' : '#222',
              border: '1px solid ' + (canSubmit ? '#7f1d1d' : '#333'),
              borderRadius: 4,
              color: canSubmit ? '#fca5a5' : '#555',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
            <XCircle size={12} /> Reject
          </button>
        </div>
      </div>
    </div>
  )
}

export function RejectUndoToast({ hook }) {
  if (!hook.undoToast) return null
  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
      background: '#0f172a', border: '1px solid #7f1d1d',
      borderRadius: 6, padding: '10px 14px',
      display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 4px 12px rgba(0,0,0,0.5)', maxWidth: 480,
    }}>
      <XCircle size={14} style={{ color: '#fca5a5' }} />
      <div style={{ fontSize: 12, color: '#ddd', flex: 1 }}>
        Rejected ({hook.undoToast.category}): <span style={{ color: '#fca5a5' }}>{hook.undoToast.label}</span>
      </div>
      <button onClick={hook.undo}
        style={{
          padding: '4px 10px', background: 'transparent',
          border: '1px solid #555', borderRadius: 4, color: '#bfdbfe',
          cursor: 'pointer', fontSize: 12,
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
        <Undo2 size={11} /> Undo
      </button>
    </div>
  )
}

// Reusable Reject button. Surfaces can use it OR build their own button —
// the important thing is that they call hook.start(m) on click.
export function RejectButton({ onClick, size = 'md' }) {
  const padding = size === 'sm' ? '2px 8px' : '4px 10px'
  const fontSize = size === 'sm' ? 11 : 12
  return (
    <button
      onClick={onClick}
      title="Reject — hide now AND skip on future TBTV-AI re-runs of this video"
      style={{
        padding, fontSize,
        background: 'transparent', border: '1px solid #7f1d1d',
        borderRadius: 4, color: '#fca5a5', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
      <XCircle size={size === 'sm' ? 10 : 11} /> Reject
    </button>
  )
}
